import { useEffect, useState } from 'react';
import { useApp } from '../state/AppState.jsx';
import { supabase, unwrap } from '../lib/supabase.js';
import { navigate } from '../lib/router.js';
import { relativeTime, todayISO, diffDays, dueLabel } from '../lib/dates.js';
import * as Icon from '../lib/icons.jsx';
import { PRESENCE_LABELS, Avatar, EmptyState, Loading } from '../components/ui.jsx';
import { ChartCard, SegmentedBar, HBars, ProgressRing } from '../components/Charts.jsx';

export default function Dashboard() {
  const app = useApp();
  const [report, setReport] = useState(null);
  const [activity, setActivity] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!app.workspaceId) return;
    let live = true;
    setLoading(true);
    (async () => {
      const [reportData, pulseData, mine] = await Promise.all([
        app.guard(async () => unwrap(await supabase.rpc('get_board_reports', {
          p_workspace_id: app.workspaceId,
        })), { silent: true }),
        app.guard(async () => unwrap(await supabase.rpc('get_activity_feed', {
          p_workspace_id: app.workspaceId,
        })), { silent: true }),
        // Same RPC My Work uses, so the two views can never disagree about
        // what is assigned to you.
        app.guard(async () => unwrap(await supabase.rpc('get_my_work')), { silent: true }),
      ]);
      if (!live) return;
      setReport(reportData);
      // get_activity_feed returns the array directly, not { activity: [...] }.
      setActivity(pulseData || []);
      setMyTasks((mine || []).filter((t) => t.workspaceId === app.workspaceId));
      setLoading(false);
    })();
    return () => { live = false; };
  }, [app.workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !report) return <div className="scroll-y"><Loading label="Building your dashboard" /></div>;

  const { summary, statusBreakdown, boardProgress, workload } = report;
  const topWorkload = workload.filter((person) => person.total > 0).slice(0, 6);
  const myOverdue = myTasks.filter((task) => !task.done && task.due && task.due < todayISO()).length;

  return (
    <div className="scroll-y">
      <div className="page">
        <div className="page-head">
          <h1 className="page-title">
            {greeting()}, {app.user.name.split(' ')[0]}
          </h1>
          <p className="page-sub">
            {summary.total} item{summary.total === 1 ? '' : 's'} across {summary.boards} board
            {summary.boards === 1 ? '' : 's'} in {app.workspace?.name}.
          </p>
        </div>

        <div className="stat-grid" style={{ marginBottom: 18 }}>
          <Stat
            label="Completed"
            icon={Icon.Check}
            value={summary.done}
            foot={`${summary.completionRate}% of all work`}
          />
          <Stat
            label="In progress"
            icon={Icon.Clock}
            value={summary.active}
            foot={`${summary.noDueDate} without a due date`}
          />
          <Stat
            label="Due this week"
            icon={Icon.Calendar}
            value={summary.dueThisWeek}
            foot="Next seven days"
            tone={summary.dueThisWeek ? 'warn' : undefined}
          />
          {/* These four count the whole workspace; the list underneath is only
              what is assigned to you. Saying how many of the overdue items are
              yours is what stops "it says 2 and I can see 1" - both numbers
              were right, they were answering different questions. */}
          <Stat
            label="Overdue"
            icon={Icon.Alert}
            value={summary.overdue}
            foot={summary.overdue
              ? `${myOverdue} assigned to you, across every board`
              : 'Nothing is late'}
            tone={summary.overdue ? 'danger' : 'good'}
          />
        </div>

        <MyTasks items={myTasks} />

        {/* Two columns on a desktop; a class, not an inline style, because the
            phone needs to collapse it to one - inline, this is what squeezed
            the whole dashboard into two cramped strips at 375pt. */}
        <div className="grid dash-cols">
          <div className="grid">
            <ChartCard
              title="Where the work stands"
              note="Every item on every board, by status."
              table={{
                columns: ['Status', 'Items', 'Share'],
                rows: statusBreakdown.map((status) => [
                  status.label,
                  status.count,
                  `${Math.round((status.count / Math.max(1, summary.total)) * 100)}%`,
                ]),
              }}
            >
              <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
                <ProgressRing percent={summary.completionRate} label="complete" />
                <div style={{ flex: 1, minWidth: 240 }}>
                  <SegmentedBar
                    total={summary.total}
                    segments={statusBreakdown.map((status) => ({
                      id: status.id,
                      label: status.label,
                      value: status.count,
                      color: status.color,
                    }))}
                  />
                </div>
              </div>
            </ChartCard>

            <ChartCard
              title="Boards"
              note="Sorted by size. Click through to open one."
              table={{
                columns: ['Board', 'Items', 'Done', 'Overdue', 'Progress'],
                rows: boardProgress.map((board) => [
                  board.name, board.total, board.done, board.overdue, `${board.percent}%`,
                ]),
              }}
              right={(
                <button type="button" className="btn sm" onClick={() => navigate('/reports')}>
                  Full report
                </button>
              )}
            >
              {boardProgress.length
                ? (
                  <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(238px, 1fr))', gap: 12 }}>
                    {boardProgress.map((board) => (
                      <button
                        key={board.id}
                        type="button"
                        className="board-card"
                        onClick={() => navigate(`/boards/${board.id}`)}
                      >
                        <div className="board-card-top">
                          <span className="board-dot" style={{ background: board.color }} />
                          <span className="board-card-name">{board.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 10 }}>
                          <span style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.02em' }}>
                            {board.percent}%
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                            {board.done} of {board.total} done
                          </span>
                        </div>
                        <div className="progress" style={{ marginTop: 8 }}>
                          <i style={{ width: `${board.percent}%`, background: board.color }} />
                        </div>
                        {board.overdue > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <span className="chip danger">
                              <Icon.Alert size={12} /> {board.overdue} overdue
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )
                : (
                  <EmptyState
                    icon={Icon.Grid}
                    title="No boards yet"
                    text="Create a board from the sidebar to start tracking work."
                  />
                )}
            </ChartCard>

            <ChartCard
              title="Team workload"
              note="Open work per person, split by state. Hover a bar for the breakdown."
              table={{
                columns: ['Person', 'Assigned', 'Done', 'Active', 'Overdue'],
                rows: topWorkload.map((person) => [
                  person.name, person.total, person.done, person.active, person.overdue,
                ]),
              }}
              right={(
                <button type="button" className="btn sm" onClick={() => navigate('/people')}>People</button>
              )}
            >
              {topWorkload.length
                ? (
                  <>
                    <HBars
                      rows={topWorkload.map((person) => ({
                        id: person.id || 'unassigned',
                        label: person.name,
                        sublabel: person.title,
                        value: person.total,
                        meta: `${person.active} active, ${person.done} done, ${person.overdue} overdue`,
                        avatar: <Avatar person={person.id ? person : null} size="xs" />,
                        segments: [
                          { id: 'done', value: person.done, color: 'var(--series-3)' },
                          { id: 'active', value: person.active - person.overdue, color: 'var(--series-1)' },
                          { id: 'overdue', value: person.overdue, color: 'var(--series-8)' },
                        ],
                      }))}
                    />
                    <div className="legend">
                      <span className="legend-item">
                        <span className="legend-swatch" style={{ background: 'var(--series-3)' }} />Done
                      </span>
                      <span className="legend-item">
                        <span className="legend-swatch" style={{ background: 'var(--series-1)' }} />On track
                      </span>
                      <span className="legend-item">
                        <span className="legend-swatch" style={{ background: 'var(--series-8)' }} />Overdue
                      </span>
                    </div>
                  </>
                )
                : <EmptyState icon={Icon.Users} title="Nothing assigned yet" text="Assign items to see workload here." />}
            </ChartCard>
          </div>

          <div className="grid">
          {/* Who is around right now, before you go asking someone for
              something. Sorted so the people you can actually reach lead, and
              anyone on leave says so in words - the whole point of declaring
              it. */}
          <section className="card">
            <div className="card-head">
              <div className="card-title">Team</div>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {[...app.directory]
                .sort((a, b) => {
                  const order = { available: 0, busy: 1, away: 2, leave: 3, offline: 4 };
                  return order[app.presenceOf(a.id)] - order[app.presenceOf(b.id)]
                    || a.name.localeCompare(b.name);
                })
                .slice(0, 8)
                .map((member) => {
                  const status = app.presenceOf(member.id);
                  return (
                    <div key={member.id} className="member-row" style={{ padding: '6px 0' }}>
                      <Avatar person={member} size="sm" presence={status} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                        {member.name}
                        {member.id === app.user.id ? ' (you)' : ''}
                      </span>
                      <span className={`presence-text ${status}`}>{PRESENCE_LABELS[status]}</span>
                    </div>
                  );
                })}
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <div className="card-title">Recent activity</div>
            </div>
            <div className="card-body" style={{ paddingTop: 6 }}>
              {activity.length
                ? (
                  <div className="feed">
                    {activity.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        className="feed-row"
                        style={{ border: 0, background: 'none', textAlign: 'left', cursor: 'pointer', width: '100%' }}
                        onClick={() => navigate(
                          entry.itemId
                            ? `/boards/${entry.boardId}?item=${entry.itemId}`
                            : `/boards/${entry.boardId}`,
                        )}
                      >
                        <Avatar person={entry.user} size="xs" />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span className="feed-text" style={{ display: 'block' }}>
                            {describe(entry)}
                          </span>
                          <span className="feed-time" style={{ display: 'block' }}>
                            {entry.boardName} · {relativeTime(entry.createdAt)}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )
                : <EmptyState icon={Icon.Clock} title="Quiet in here" text="Changes across the workspace show up here." />}
            </div>
          </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, foot, icon: IconComponent, tone }) {
  const color = tone === 'danger' ? 'var(--danger)'
    : tone === 'warn' ? 'var(--warn)'
      : tone === 'good' ? 'var(--good)'
        : 'var(--text)';
  return (
    <div className="stat">
      <div className="stat-label">
        <IconComponent size={14} style={{ color }} />
        {label}
      </div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-foot">{foot}</div>
    </div>
  );
}

/**
 * Everything assigned to the signed-in user in this workspace.
 *
 * Ordered by urgency rather than by board: overdue first, then today, then the
 * rest by due date, with undated work last. A dashboard list sorted by board
 * makes you scan every board to find what is late.
 */
function MyTasks({ items }) {
  const open = items.filter((task) => !task.done);
  const today = todayISO();

  const rank = (task) => {
    if (!task.due) return 3;
    const delta = diffDays(today, task.due);
    if (delta < 0) return 0;
    if (delta === 0) return 1;
    return 2;
  };
  const sorted = [...open].sort((a, b) => (
    rank(a) - rank(b) || String(a.due || '9999').localeCompare(String(b.due || '9999'))
  ));

  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <div className="card-head">
        <div>
          <div className="card-title">Assigned to me</div>
          <div className="card-sub">
            {open.length ? `${open.length} open item${open.length === 1 ? '' : 's'} across this workspace.`
              : 'Nothing assigned to you right now.'}
          </div>
        </div>
        <button type="button" className="btn sm" onClick={() => navigate('/my-work')}>
          Open My work
        </button>
      </div>
      <div className="card-body">
        {!sorted.length && (
          <EmptyState
            icon={Icon.Check}
            title="Nothing on your plate"
            text="Items assigned to you in this workspace show up here."
          />
        )}
        {/* Reuses the member-row / board-dot / chip classes MyWork already
            uses, so this section matches the rest of the app without any new
            CSS. */}
        {sorted.slice(0, 8).map((task) => {
          // dueLabel returns { text, tone }, not a string. Rendering the object
          // itself threw React error #31 ("Objects are not valid as a React
          // child") and, being on the default route, crashed the app for
          // everyone on every sign-in. Every other caller reads .text - use the
          // tone it already computed rather than deriving overdue again.
          const due = task.due ? dueLabel(task.due) : null;
          return (
            <div
              key={task.id}
              className="member-row task-row"
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/boards/${task.boardId}?item=${task.id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/boards/${task.boardId}?item=${task.id}`);
                }
              }}
            >
              <span className="board-dot" style={{ background: task.boardColor }} />
              <div className="task-row-main">
                <div className="member-name">{task.name}</div>
                <div className="member-mail">{task.boardName}</div>
              </div>
              {task.status && (
                <span className="chip" style={{ background: task.status.color, color: '#fff' }}>
                  {task.status.label}
                </span>
              )}
              <span
                className="tag"
                style={due?.tone === 'overdue' ? { color: 'var(--danger)' } : undefined}
              >
                {due ? due.text : 'No date'}
              </span>
            </div>
          );
        })}
        {sorted.length > 8 && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)', paddingTop: 8 }}>
            {sorted.length - 8} more in My work.
          </div>
        )}
      </div>
    </section>
  );
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function describe(entry) {
  const who = entry.user?.name || 'Someone';
  switch (entry.kind) {
    case 'item_created': return <><b className="feed-name">{who}</b> created <b>{entry.payload.name}</b></>;
    case 'item_deleted': return <><b className="feed-name">{who}</b> deleted <b>{entry.payload.name}</b></>;
    case 'item_renamed': return <><b className="feed-name">{who}</b> renamed an item to <b>{entry.payload.to}</b></>;
    case 'item_moved': return <><b className="feed-name">{who}</b> moved <b>{entry.itemName}</b> to {entry.payload.to}</>;
    case 'board_created': return <><b className="feed-name">{who}</b> created the board <b>{entry.payload.name}</b></>;
    case 'column_added': return <><b className="feed-name">{who}</b> added the <b>{entry.payload.title}</b> column</>;
    case 'file_added': return <><b className="feed-name">{who}</b> attached <b>{entry.payload.filename}</b></>;
    case 'cell_changed':
      return (
        <>
          <b className="feed-name">{who}</b> set {entry.payload.columnTitle} on <b>{entry.itemName}</b>
          {' to '}<b>{entry.payload.to}</b>
        </>
      );
    default: return <><b className="feed-name">{who}</b> made a change</>;
  }
}
